import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Radio } from "antd";
import {
  DownloadOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
  VerticalAlignMiddleOutlined,
} from "@ant-design/icons";
import { SbButton, SbSelect, SbInput, SbTable, SbModal, SbNotification, SbTag, SbTooltip } from "uiCore";
import PageHeader from "Components/PageHeader";
import {
  PageTransitionStyled,
  WorkspaceToolbarStyled,
  ToolbarActionsStyled,
  OrgTreeCardsStyled,
  RoleTableWrapperStyled,
  ProposedRolesHeaderStyled,
  TreeDividerStyled,
  PdfCaptureStageStyled,
  OrgChartListStyled,
  TreeContainerStyled,
  ModalFieldStyled,
  FullWidthInputNumberStyled,
  SizedInputNumberStyled,
  TooltipTargetStyled,
} from "./orgDesign.styles";
import OrgTreeCard from "./components/OrgTreeCard";
import OrgDesignChartNode from "./components/OrgDesignChartNode";
import RoleChangeLegend from "./components/RoleChangeLegend";
import OrgViewLayoutSwitch from "./components/OrgViewLayoutSwitch";
import OrgDesignSummaryTab from "./components/OrgDesignSummaryTab";
import { useOrgDesignWorkspace, useCanViewOrgDesignBudget, useCanCreateOrgRedesign, useCanUpdateOrgRedesign, useRoleReportingRows } from "./useOrgDesignData";
import { mapExternalManagers, getMergedIntoTitles } from "./orgDesignAdapters";
import useOrgTreeViewportSync from "./useOrgTreeViewportSync";
import { getDescendantIds, newRoleNodeId, newRoleStatus } from "./orgDesignTreeEngine";
import { downloadOrgDesignPdf } from "./utils/orgDesignPdf";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import { showSbConfirm } from "Utils/sbModal";
import useAuthStore from "Store/useAuthStore";
import {
  STATUS_LABELS, STATUS_COLORS, ENTRY_OPTION, getFilters, getRoleChangeConfig, ORG_DESIGN_WORKSPACE_FROM,
  CROSS_PAGE_TOUR_PHASE, WORKSPACE_TOUR_STEPS, WORKSPACE_VIEW, ORG_VIEW_LAYOUT,
  isAbsorptionNewRole, describeFusionRef,
} from "./orgDesign.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { TourTargetStyled } from "uiCore/SbTour/SbTour.styles";

const { Text } = Typography;

const VIEW_OPTIONS = [
  { label: "Table View", value: WORKSPACE_VIEW.TABLE },
  { label: "Org View", value: WORKSPACE_VIEW.ORG },
  { label: "Summary", value: WORKSPACE_VIEW.SUMMARY },
];

const EVENTS = ANALYTICS_EVENTS.ORG_DESIGN;

// Shown for a manager_id that resolves to no known role or title — never the
// raw id itself.
const UNKNOWN_MANAGER_LABEL = "Unknown manager";

// Waits two repaints — used so the off-screen PDF capture stage (mounted
// fresh on Download PDF; see `pdfExporting` below) has actually been laid
// out before html2canvas measures it. Same technique OrgTreeCard.jsx uses
// before its own PNG export.
const nextPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

// Org Redesign v2: the roles a fusion merged, one line each (title · level ·
// function, already resolved onto each reference by the backend).
const FusionDetails = ({ heading, refs }) => (
  <>
    <div>{heading}</div>
    {refs.map((ref) => (
      <div key={ref.sb_unique_id || ref.job_id || ref.title}>{describeFusionRef(ref)}</div>
    ))}
  </>
);

// The Classification tag, with a tooltip only when there's fusion detail to
// show — every other row renders exactly the tag it always did.
const ClassificationTag = ({ label, color, details }) =>
  details ? (
    <SbTooltip content={details}>
      <TooltipTargetStyled>
        <SbTag color={color}>{label}</SbTag>
      </TooltipTargetStyled>
    </SbTooltip>
  ) : (
    <SbTag color={color}>{label}</SbTag>
  );

const OrgDesignWorkspace = () => {
  const navigate = useNavigate();
  const {
    draft, budgetSyncing, scheduleBudgetSync, updateHeadcount, updateReportingLine, renameRole,
    addRole, removeNewRole, updateNewRoleHeadcount, updateNewRoleManager,
    setTargetBudget, saveVersion,
  } = useOrgDesignWorkspace();
  const track = useAnalytics(ORG_DESIGN_WORKSPACE_FROM);
  const canViewBudget = useCanViewOrgDesignBudget();
  const canCreateOrgRedesign = useCanCreateOrgRedesign();
  const canUpdateOrgRedesign = useCanUpdateOrgRedesign();
  const roleReportingRows = useRoleReportingRows();
  // "Prepared by" on the PDF report's header — falls back to email when the
  // profile has no display name set.
  const preparedBy = useAuthStore((s) => s.profileName || s.email);
  // The off-screen, fully expanded trees the PDF captures (see
  // PdfCaptureStageStyled below).
  const pdfCurrentTreeRef = useRef(null);
  const pdfProposedTreeRef = useRef(null);
  // The on-screen Org View cards, measured to fit their charts (see
  // fitTreesToCards below).
  const currentTreeCardRef = useRef(null);
  const proposedTreeCardRef = useRef(null);

  // Current Org starts with no proposed changes, so Table View is the more
  // useful first look; PAIC/opened versions already have a tree worth seeing.
  const [view, setView] = useState(() =>
    draft?.entryOption === ENTRY_OPTION.EXISTING_ORG ? WORKSPACE_VIEW.TABLE : WORKSPACE_VIEW.ORG
  );
  // Org View always opens stacked, so this resets every time Org View is
  // opened (see changeView) rather than being remembered.
  const [orgLayout, setOrgLayout] = useState(ORG_VIEW_LAYOUT.STACKED);
  // Expand all / Collapse all for both charts. Each press bumps `version`,
  // which remounts the trees (see OrgTreeCard): every node open when
  // `expandAll`, otherwise back to the opening state (top levels only).
  // Opening Org View starts collapsed again; switching layout keeps whatever
  // the user has expanded or collapsed.
  const [treeExpansion, setTreeExpansion] = useState({ expandAll: false, version: 0 });
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);
  // Mounts the off-screen PDF capture stage only for the span of an actual
  // export, so a large org isn't rendered twice on every workspace visit.
  const [pdfExporting, setPdfExporting] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRoleDraft, setNewRoleDraft] = useState({ title: "", managerId: "", headcount: 1, microtaskFunction: "", annualCost: 140000 });
  // Shared zoom/scroll so the Current and Proposed OrgTreeCards pan and
  // zoom in lockstep — see useOrgTreeViewportSync. Renamed onto OrgTreeCard's
  // own controlled-prop names once here rather than at each of the three
  // call sites below.
  const { zoomIn, zoomOut, fitToCards, recenter, reportUserScroll, ...treeViewportRest } = useOrgTreeViewportSync();
  // Measures whichever cards are showing a chart and fits them. Also what
  // Reset does, so Reset fits the cards as they are now (current layout and
  // expanded nodes) rather than reusing an old fit.
  const fitTreesToCards = useCallback(() => {
    fitToCards([currentTreeCardRef.current?.measureFit(), proposedTreeCardRef.current?.measureFit()]);
  }, [fitToCards]);
  const treeViewport = {
    ...treeViewportRest,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onResetView: fitTreesToCards,
    onUserScroll: reportUserScroll,
    expandAll: treeExpansion.expandAll,
    treeVersion: treeExpansion.version,
  };

  // Role Reporting roles that aren't already in this design — extra
  // "Reports To" choices (see mapExternalManagers).
  const externalManagers = useMemo(
    () => mapExternalManagers(roleReportingRows, draft?.roles || []),
    [roleReportingRows, draft?.roles]
  );
  const externalManagerById = useMemo(
    () => new Map(externalManagers.map((m) => [m.sb_unique_id, m])),
    [externalManagers]
  );
  // Memoized so every row's reparentOptionsFor() call reuses the same base
  // list instead of rebuilding it per row. Declared before `if (!draft)
  // return null` (uses draft? internally) so the hook always runs.
  const pseudoNodesForReparent = useMemo(() => {
    const nodes = [
      ...(draft?.roles || []).map((r) => ({ sb_unique_id: r.sb_unique_id, manager_id: r.manager_id })),
      ...(draft?.newRoles || []).map((nr) => ({ sb_unique_id: newRoleNodeId(nr.id), manager_id: nr.manager_id })),
    ];
    // An external manager already picked for some role links back into this
    // design through its own reporting chain — included so getDescendantIds
    // sees through it and a pick can't close a loop via that role.
    const usedManagerIds = new Set(nodes.map((n) => n.manager_id));
    externalManagers
      .filter((m) => usedManagerIds.has(m.sb_unique_id))
      .forEach((m) => nodes.push({ sb_unique_id: m.sb_unique_id, manager_id: m.entry_id }));
    return nodes;
  }, [draft?.roles, draft?.newRoles, externalManagers]);
  // Org Redesign v2: an absorbed role's tooltip names the role it merged
  // into. Memoized (and declared before `if (!draft) return null`) for the
  // same reason as pseudoNodesForReparent above.
  const mergedIntoTitles = useMemo(
    () => getMergedIntoTitles(draft?.roles, draft?.newRoles),
    [draft?.roles, draft?.newRoles]
  );

  useEffect(() => {
    if (!draft) navigate("/org-design");
  }, [draft, navigate]);

  // ── Guided tour: workspace leg (handed off from the landing page) ──────────
  // Each step names the view its target lives in, so stepping through flips
  // between Table View, Org View and Summary; finishing or dismissing
  // returns to the landing page, which resumes at its Finish step.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  // Arriving in Org View from another tab always starts stacked (see
  // orgLayout). A tour step that re-selects Org View while it's already
  // open keeps the layout the user picked.
  const changeView = (nextView) => {
    if (nextView === WORKSPACE_VIEW.ORG && view !== WORKSPACE_VIEW.ORG) {
      setOrgLayout(ORG_VIEW_LAYOUT.STACKED);
      setTreeExpansion((t) => ({ ...t, expandAll: false }));
    }
    setView(nextView);
  };

  const tourTargetRefs = {
    viewToggle: useRef(null),
    roleTable: useRef(null),
    addRole: useRef(null),
    trees: useRef(null),
    summary: useRef(null),
    saveVersion: useRef(null),
  };
  const endWorkspaceTour = () => {
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.FINISH);
    navigate("/org-design");
  };

  // One server budget sync on open so the Budget Bar/Role Count show real
  // figures immediately, not just after the first edit. `immediate: true`
  // skips the edit-debounce delay — there's no rapid edit burst to protect
  // against on mount, so that 600ms was just added wait before this data
  // ever had a chance to show up.
  useEffect(() => {
    if (draft) scheduleBudgetSync(draft, { immediate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The draft itself now survives a refresh via useOrgDesignStore's persist
  // middleware, but a closed tab still loses whatever hasn't been synced to
  // the server yet — warn before that happens, same as any editor with
  // unsaved work. Browsers show their own generic message regardless of
  // the text set here.
  useEffect(() => {
    if (!draft) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [draft]);

  // A fresh Existing Org draft has no proposed side yet — before/after start
  // identical, and showing two "different" cards that are really duplicates
  // reads as broken. Org View only splits into two cards once there's an
  // actual difference (a PAIC/opened-version draft already has one).
  // Computed before `if (!draft) return null` because the fit effect below
  // depends on it.
  const hasProposedChanges =
    !!draft &&
    (draft.newRoles.length > 0 ||
      Object.values(draft.design || {}).some((d) => d?.status && d.status !== "UNCHANGED"));

  // Each chart opens zoomed to fit its card, and re-fits whenever Org View
  // opens, its layout switches, or it goes between one chart and two. A
  // layout effect, so it measures the cards at their new size and applies
  // the fit before they paint, with no flash of the previous zoom.
  useLayoutEffect(() => {
    if (view === WORKSPACE_VIEW.ORG) fitTreesToCards();
  }, [view, orgLayout, hasProposedChanges, fitTreesToCards]);

  if (!draft) return null;

  // Excludes a node's own descendants so a role can't be moved under itself.
  // Proposed roles aren't offered: reports_to only accepts a real JD's
  // sb_unique_id (see findRolesReportingToProposedRoles for the save check).
  // currentManagerId/Label are appended as a disabled entry only when the
  // role's real manager isn't in the options list (e.g. reports outside the
  // fetched roster) — otherwise SbSelect would clear the value and fire
  // onChange(""), silently dropping the manager. Appended even with no known
  // title, so that can never happen.
  const reparentOptionsFor = (nodeId, currentManagerId, currentManagerLabel) => {
    const excluded = getDescendantIds(pseudoNodesForReparent, nodeId);
    const options = [
      ...draft.roles.filter((r) => !excluded.has(r.sb_unique_id)).map((r) => ({ value: r.sb_unique_id, label: r.job_title })),
      ...externalManagers
        .filter((m) => !excluded.has(m.sb_unique_id) && !excluded.has(m.entry_id))
        .map((m) => ({ value: m.sb_unique_id, label: m.job_title })),
    ];
    if (currentManagerId && !options.some((o) => o.value === currentManagerId)) {
      options.push({ value: currentManagerId, label: currentManagerLabel || UNKNOWN_MANAGER_LABEL, disabled: true });
    }
    return options;
  };

  // Same resolution the "Reports To" select itself uses to display a value,
  // so the column's filter dropdown lists real names/"No manager" instead of
  // raw manager_id values.
  const resolveManagerLabel = (record) => {
    if (!record.manager_id) return "No manager";
    const role = draft.roles.find((r) => r.sb_unique_id === record.manager_id);
    if (role) return role.job_title;
    const newRole = draft.newRoles.find((nr) => newRoleNodeId(nr.id) === record.manager_id);
    if (newRole) return `${newRole.role_title} (proposed)`;
    return externalManagerById.get(record.manager_id)?.job_title || record.manager_job_title || UNKNOWN_MANAGER_LABEL;
  };

  // Guards against a double-click/double-Enter on the modal's OK button
  // firing two createOrgRedesignVersion calls before either resolves.
  const handleSaveVersion = async () => {
    if (savingVersion) return;
    setSavingVersion(true);
    try {
      const { entry, serverSynced } = await saveVersion(saveLabel);
      track(EVENTS.VERSION_SAVED, {
        entryOption: draft.entryOption,
        isFirstSave,
        serverSynced: !!serverSynced,
        roleCount: draft.roles.length,
        newRoleCount: draft.newRoles.length,
      });
      setSaveModalOpen(false);
      setSaveLabel("");
      // Only shown alongside a real server sync — saveVersion already surfaces
      // its own warning toast when that fails, and showing "Version saved" on
      // top of it would contradict that warning.
      if (entry && serverSynced) SbNotification.success("Version saved", `Saved as ${entry.label}.`);
    } finally {
      setSavingVersion(false);
    }
  };

  // Generates the Current / Proposed / Summary report and saves it straight
  // to disk — no print dialog, and independent of whether Org View or Table
  // View is open. Mounts the off-screen capture stage, waits for it (and any
  // web fonts) to paint, then hands the fully expanded trees to
  // downloadOrgDesignPdf. Guarded against a double-click the same way
  // handleSaveVersion is.
  const handleDownloadPdf = async () => {
    if (pdfExporting) return;
    track(EVENTS.DOWNLOAD_PDF, { view, entryOption: draft.entryOption, hasProposedChanges });
    setPdfExporting(true);
    try {
      await nextPaint();
      await document.fonts?.ready;
      await downloadOrgDesignPdf({
        draft,
        currentTreeNode: draft.before_tree.ch.length ? pdfCurrentTreeRef.current : null,
        proposedTreeNode: hasProposedChanges && draft.after_tree.ch.length ? pdfProposedTreeRef.current : null,
        canViewBudget,
        hasProposedChanges,
        preparedBy,
      });
    } catch (error) {
      console.error("Error generating Organisation Design PDF:", error);
      SbNotification.error("Download failed", "We couldn't generate the PDF. Please try again.");
    } finally {
      setPdfExporting(false);
    }
  };

  // Proactive, matching the pattern the landing page's entry cards already
  // use — surfaces the real permission state before the user opens the
  // modal, rather than only after they click through it and get a toast.
  const isFirstSave = !draft.orgRedesignId;
  const canSaveVersion = isFirstSave ? canCreateOrgRedesign : canUpdateOrgRedesign;
  // The label is sent to the server as org_redesign_name (see saveVersion)
  // on a CURRENT ORG draft's first save and on every PAIC save — any other
  // save keeps it local, so its modal copy stays generic.
  const isNamingVersion =
    draft.entryOption === ENTRY_OPTION.PAIC_RECOMMENDED || (isFirstSave && draft.entryOption === ENTRY_OPTION.EXISTING_ORG);

  const handleAddRole = () => {
    if (!newRoleDraft.title || !newRoleDraft.managerId) {
      SbNotification.warning("Missing fields", "Title and reporting line are required.");
      return;
    }
    const manager = draft.roles.find((r) => r.sb_unique_id === newRoleDraft.managerId);
    track(EVENTS.PROPOSED_ROLE_ADDED, { headcount: newRoleDraft.headcount });
    addRole({
      title: newRoleDraft.title,
      managerId: newRoleDraft.managerId,
      headcount: newRoleDraft.headcount,
      microtaskFunction: manager?.microtask_function || newRoleDraft.microtaskFunction,
      annualCost: newRoleDraft.annualCost,
    });
    setAddRoleOpen(false);
    setNewRoleDraft({ title: "", managerId: "", headcount: 1, microtaskFunction: "", annualCost: 140000 });
  };

  const roleOptions = draft.roles.map((r) => ({ value: r.sb_unique_id, label: r.job_title }));

  // Wrapped rather than tracked inline: the Role cell fires the same rename
  // from both onBlur and onPressEnter, so one wrapper keeps them in step.
  const handleRenameRole = (record, value) => {
    track(EVENTS.ROLE_RENAMED, { roleId: record.sb_unique_id });
    renameRole(record.sb_unique_id, value);
  };

  const handleChangeOrgLayout = (nextLayout) => {
    track(EVENTS.LAYOUT_TOGGLED, { layout: nextLayout });
    setOrgLayout(nextLayout);
  };

  // Expand all opens every node in both charts; Collapse all returns both
  // to the opening state (top role and its direct reports). Both keep the
  // zoom and re-center the charts on their new size.
  const handleTreeExpansion = (expandAll) => {
    track(EVENTS.TREES_EXPANSION_CHANGED, { expandAll });
    setTreeExpansion((t) => ({ expandAll, version: t.version + 1 }));
    recenter();
  };

  const handleSetTargetBudget = (value) => {
    // The figure itself is deliberately not sent — no other module sends
    // money, and the useful signal is that a target was set at all.
    track(EVENTS.TARGET_BUDGET_SET, { cleared: value == null });
    setTargetBudget(value);
  };

  // Shared by the design cells and their column filters.
  const getDesignStatus = (record) => draft.design[record.sb_unique_id]?.status || "UNCHANGED";
  const getAfterHc = (record) => draft.design[record.sb_unique_id]?.after_hc ?? record.total_positions;
  const getFusionDetails = (record) => {
    if (record.fused_from?.length) return <FusionDetails heading="Absorbed these roles:" refs={record.fused_from} />;
    const mergedInto = mergedIntoTitles.get(record.sb_unique_id) || mergedIntoTitles.get(record.job_id);
    return mergedInto ? `Merged into ${mergedInto}` : null;
  };

  const tableColumns = [
    {
      title: "Role",
      dataIndex: "job_title",
      filters: getFilters(draft.roles, "job_title"),
      onFilter: (value, record) => record.job_title === value,
      render: (title, record) => (
        <SbInput
          size="small"
          variant="borderless"
          defaultValue={title}
          onBlur={(e) => e.target.value !== title && handleRenameRole(record, e.target.value)}
          onPressEnter={(e) => e.target.value !== title && handleRenameRole(record, e.target.value)}
        />
      ),
    },
    {
      title: "Function",
      dataIndex: "microtask_function",
      filters: getFilters(draft.roles, "microtask_function"),
      onFilter: (value, record) => record.microtask_function === value,
    },
    {
      title: "Reports To",
      dataIndex: "manager_id",
      filters: getFilters(draft.roles, "manager_id", resolveManagerLabel),
      onFilter: (value, record) => resolveManagerLabel(record) === value,
      render: (managerId, record) => (
        <SbSelect
          size="small"
          variant="borderless"
          value={managerId || undefined}
          placeholder="No manager"
          allowClear
          popupMatchSelectWidth={220}
          showSearch={{ optionFilterProp: "label" }}
          options={reparentOptionsFor(record.sb_unique_id, managerId, resolveManagerLabel(record))}
          onChange={(value) => {
            track(EVENTS.ROLE_REPARENTED, { roleId: record.sb_unique_id, cleared: !value });
            updateReportingLine(record.sb_unique_id, value || null, externalManagerById.get(value));
          }}
        />
      ),
    },
    {
      title: "Current Positions",
      dataIndex: "total_positions",
    },
    {
      title: "Classification",
      key: "status",
      filterValue: getDesignStatus,
      filterLabel: (status) => getRoleChangeConfig(status, draft.entryOption).label,
      // Read-only: classification is derived from the Target Positions edit
      // (see updateHeadcount), not a user choice — same getRoleChangeConfig
      // lookup that drives the tree nodes and legend renders it here too.
      render: (_, record) => {
        const status = getDesignStatus(record);
        const { label, color } = getRoleChangeConfig(status, draft.entryOption);
        return <ClassificationTag label={label} color={color} details={getFusionDetails(record)} />;
      },
    },
    {
      title: "Target Positions",
      key: "afterHc",
      filterValue: getAfterHc,
      render: (_, record) => {
        const afterHc = getAfterHc(record);
        return (
          <SizedInputNumberStyled
            $width={90}
            size="small"
            min={0}
            value={afterHc}
            onChange={(value) => updateHeadcount(record.sb_unique_id, value)}
            onBlur={() => {
              const committed = draft.design[record.sb_unique_id]?.after_hc;
              if (committed != null && committed !== record.total_positions) {
                track(EVENTS.ROLE_HEADCOUNT_CHANGED, {
                  roleId: record.sb_unique_id,
                  before: record.total_positions,
                  after: committed,
                });
              }
            }}
          />
        );
      },
    },
  ];

  const newRoleColumns = [
    { title: "Role", dataIndex: "role_title" },
    { title: "Function", dataIndex: "microtask_function" },
    {
      title: "Reports To",
      dataIndex: "manager_id",
      filterValue: resolveManagerLabel,
      render: (managerId, record) => (
        <SbSelect
          size="small"
          variant="borderless"
          value={managerId || undefined}
          placeholder="No manager"
          allowClear
          popupMatchSelectWidth={false}
          showSearch={{ optionFilterProp: "label" }}
          options={reparentOptionsFor(newRoleNodeId(record.id), managerId, resolveManagerLabel(record))}
          onChange={(value) => updateNewRoleManager(record.id, value || null, externalManagerById.get(value))}
        />
      ),
    },
    // A proposed role never existed "before", so Current Positions is
    // always 0 and Status is Created — or Fused for an Org Redesign v2
    // FUSED role (never an editable dropdown).
    { title: "Current Positions", key: "currentPositions", filterValue: () => 0, render: () => 0 },
    {
      title: "Classification",
      key: "status",
      filterValue: (record) => STATUS_LABELS[newRoleStatus(record)],
      render: (_, record) => {
        const status = newRoleStatus(record);
        const details = record.replaces?.length ? (
          <FusionDetails heading="Combines these roles:" refs={record.replaces} />
        ) : null;
        return <ClassificationTag label={STATUS_LABELS[status]} color={STATUS_COLORS[status]} details={details} />;
      },
    },
    {
      title: "Target Positions",
      dataIndex: "after_hc",
      // An ABSORPTION entry only records a merge — the surviving role keeps
      // its own seat — so its 0 is shown, never edited into a new seat.
      render: (hc, record) => isAbsorptionNewRole(record) ? hc : (
        <SizedInputNumberStyled
          $width={90}
          size="small"
          min={1}
          value={hc}
          onChange={(value) => updateNewRoleHeadcount(record.id, value)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <SbButton
          buttonType="action"
          icon={<DeleteOutlined />}
          onClick={() =>
            showSbConfirm({
              title: "Delete this proposed role?",
              content: `"${record.role_title}" will be removed from this Organisation Design. This can't be undone.`,
              okText: "Delete",
              cancelText: "Cancel",
              okButtonProps: { danger: true },
              onOk: () => {
                track(EVENTS.PROPOSED_ROLE_REMOVED, { roleId: record.id });
                removeNewRole(record.id);
              },
            })
          }
        />
      ),
    },
  ];

  return (
    <PageTransitionStyled>
      <PageHeader
        title="Organisation Design Workspace"
        subtitle={draft.versionLabel || draft.sourceLabel}
        onBack={() => navigate("/org-design")}
        backLabel="Back to Organisation Design"
      />

      <WorkspaceToolbarStyled>
        <TourTargetStyled ref={tourTargetRefs.viewToggle}>
          <Radio.Group
            value={view}
            onChange={(e) => {
              track(EVENTS.VIEW_TOGGLED, { view: e.target.value });
              changeView(e.target.value);
            }}
            options={VIEW_OPTIONS}
            optionType="button"
          />
        </TourTargetStyled>

        <ToolbarActionsStyled ref={tourTargetRefs.saveVersion}>
          {/* Held while a budget/role-count sync is in flight — the PDF's
              Summary page would otherwise capture stale or not-yet-loaded
              figures that the on-screen Budget Bar is itself hiding. */}
          <SbTooltip content="Updating figures — the PDF will be available in a moment." disabled={!budgetSyncing || pdfExporting}>
            <TooltipTargetStyled>
              <SbButton
                buttonType="secondary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadPdf}
                loading={pdfExporting}
                disabled={budgetSyncing}
              >
                Download PDF
              </SbButton>
            </TooltipTargetStyled>
          </SbTooltip>
          <SbTooltip content="You don't have permission to save this Organisation Design version." disabled={canSaveVersion}>
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" icon={<SaveOutlined />} onClick={() => setSaveModalOpen(true)} disabled={!canSaveVersion}>
                Save Version
              </SbButton>
            </TooltipTargetStyled>
          </SbTooltip>
        </ToolbarActionsStyled>
      </WorkspaceToolbarStyled>

      {view === WORKSPACE_VIEW.ORG && (
        <RoleChangeLegend
          entryOption={draft.entryOption}
          actions={
            <>
              <OrgViewLayoutSwitch layout={orgLayout} onChange={handleChangeOrgLayout} disabled={!hasProposedChanges} />
              <ToolbarActionsStyled>
                <SbButton buttonType="secondary" size="small" icon={<ColumnHeightOutlined />} onClick={() => handleTreeExpansion(true)}>
                  Expand all
                </SbButton>
                <SbButton buttonType="secondary" size="small" icon={<VerticalAlignMiddleOutlined />} onClick={() => handleTreeExpansion(false)}>
                  Collapse all
                </SbButton>
              </ToolbarActionsStyled>
            </>
          }
        />
      )}

      {/* The same elements in both layouts, so switching layout keeps the
          cards mounted: expanded and collapsed nodes are kept, and only the
          arrangement and the fit change. */}
      {view === WORKSPACE_VIEW.ORG && hasProposedChanges && (
        <OrgTreeCardsStyled ref={tourTargetRefs.trees} $layout={orgLayout}>
          <OrgTreeCard
            ref={currentTreeCardRef}
            title="Current"
            variant="current"
            rootChildren={draft.before_tree.ch}
            exportFilename="org-design-current"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
          <TreeDividerStyled $layout={orgLayout} />
          <OrgTreeCard
            ref={proposedTreeCardRef}
            title="Proposed"
            variant="proposed"
            rootChildren={draft.after_tree.ch}
            exportFilename="org-design-proposed"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
        </OrgTreeCardsStyled>
      )}

      {view === WORKSPACE_VIEW.ORG && !hasProposedChanges && (
        <OrgTreeCardsStyled ref={tourTargetRefs.trees} $layout={orgLayout}>
          <OrgTreeCard
            ref={currentTreeCardRef}
            title="Org Structure"
            variant="current"
            rootChildren={draft.before_tree.ch}
            exportFilename="org-design-current"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
        </OrgTreeCardsStyled>
      )}

      {view === WORKSPACE_VIEW.TABLE && (
        <RoleTableWrapperStyled ref={tourTargetRefs.roleTable}>
          <ProposedRolesHeaderStyled>
            <Text strong>Proposed / Added Roles</Text>
            <SbTooltip content="You don't have permission to add a role without compensation data." disabled={canViewBudget}>
              <TooltipTargetStyled ref={tourTargetRefs.addRole}>
                <SbButton buttonType="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddRoleOpen(true)} disabled={!canViewBudget}>
                  Add Role
                </SbButton>
              </TooltipTargetStyled>
            </SbTooltip>
          </ProposedRolesHeaderStyled>
          {draft.newRoles.length > 0 && (
            <SbTable columns={newRoleColumns} dataSource={draft.newRoles} rowKey="id" pagination={false} size="small" />
          )}

          <ProposedRolesHeaderStyled>
            <Text strong>Roles</Text>
          </ProposedRolesHeaderStyled>
          <SbTable columns={tableColumns} dataSource={draft.roles} rowKey="sb_unique_id" pagination={false} size="small" />
        </RoleTableWrapperStyled>
      )}

      {view === WORKSPACE_VIEW.SUMMARY && (
        <OrgDesignSummaryTab
          ref={tourTargetRefs.summary}
          draft={draft}
          canViewBudget={canViewBudget}
          budgetSyncing={budgetSyncing}
          onSetTargetBudget={handleSetTargetBudget}
        />
      )}

      {/* Workspace leg of the Organisation Design tour (handed off from the landing page) */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.WORKSPACE}
        ready
        steps={WORKSPACE_TOUR_STEPS}
        resolveTarget={(step) => tourTargetRefs[step.key]?.current}
        onStepChange={(step) => step.view && changeView(step.view)}
        onNext={endWorkspaceTour}
        onClose={endWorkspaceTour}
      />

      {/* Off-screen, fully expanded Current/Proposed trees for the PDF to
          capture (see PdfCaptureStageStyled / utils/orgDesignPdf.jsx) —
          mounted only while a download is being generated. */}
      {pdfExporting && (
        <PdfCaptureStageStyled>
          <TreeContainerStyled ref={pdfCurrentTreeRef}>
            <OrgChartListStyled>
              {draft.before_tree.ch.map((node) => (
                <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} entryOption={draft.entryOption} />
              ))}
            </OrgChartListStyled>
          </TreeContainerStyled>
          {/* Only needed when the PDF gets a separate Proposed page. */}
          {hasProposedChanges && (
            <TreeContainerStyled ref={pdfProposedTreeRef}>
              <OrgChartListStyled>
                {draft.after_tree.ch.map((node) => (
                  <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} entryOption={draft.entryOption} />
                ))}
              </OrgChartListStyled>
            </TreeContainerStyled>
          )}
        </PdfCaptureStageStyled>
      )}

      {/* ── Save Version ── */}
      <SbModal
        open={saveModalOpen}
        title="Save Version"
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSaveVersion}
        okText="Save"
        confirmLoading={savingVersion}
      >
        <SbInput
          placeholder={isNamingVersion ? "Project name" : "Version label (optional)"}
          value={saveLabel}
          onChange={(e) => setSaveLabel(e.target.value)}
        />
      </SbModal>

      {/* ── Add Role ── */}
      <SbModal open={addRoleOpen} title="Add Proposed Role" onCancel={() => setAddRoleOpen(false)} onOk={handleAddRole} okText="Add">
        <ModalFieldStyled>
          <SbInput
            placeholder="Role title"
            value={newRoleDraft.title}
            onChange={(e) => setNewRoleDraft((d) => ({ ...d, title: e.target.value }))}
          />
        </ModalFieldStyled>
        <ModalFieldStyled>
          <SbSelect
            placeholder="Reports to"
            options={roleOptions}
            value={newRoleDraft.managerId || undefined}
            onChange={(value) => setNewRoleDraft((d) => ({ ...d, managerId: value }))}
          />
        </ModalFieldStyled>
        <ModalFieldStyled>
          <FullWidthInputNumberStyled
            min={1}
            value={newRoleDraft.headcount}
            onChange={(value) => setNewRoleDraft((d) => ({ ...d, headcount: value }))}
            addonBefore="Headcount"
          />
        </ModalFieldStyled>
        {canViewBudget && (
        <FullWidthInputNumberStyled
          min={0}
          step={5000}
          value={newRoleDraft.annualCost}
          onChange={(value) => setNewRoleDraft((d) => ({ ...d, annualCost: value }))}
          addonBefore="Budget (Annual $/FTE)"
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value.replace(/,*/g, "")}
        />
        )}
      </SbModal>
    </PageTransitionStyled>
  );
};

export default OrgDesignWorkspace;
